import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle, XCircle, AlertCircle, Share2, RefreshCcw,
  Check, ChevronDown, ChevronUp,
  ArrowLeft, Loader2, Send, Upload
} from 'lucide-react';
import { API_ROOT } from '@/config';
import SEO from '@/components/seo';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface AnalysisResult {
  unique_id?: string;
  compliance_score: number;
  passing?: boolean;
  compliant: any[];
  non_compliant: any[];
  campaign_name?: string;
  campaign_uuid?: string;
  creator_name?: string;
  creator_email?: string;
  created_at?: string;
  video_url?: string;
  approval_status?: string;
  submission_status?: string;
  submitted_at?: string;
}

// Extract display text from a requirement item (can be string or object from external API)
function extractRequirementText(item: any): string {
  if (!item) return '';
  if (typeof item === 'string') {
    let text = item;
    if (text.startsWith('Element: ')) text = text.substring(9);
    else if (text.startsWith('Element:')) text = text.substring(8);
    return text.trim();
  }
  if (typeof item === 'object') {
    const text = item.input || item.requirement || item.element || item.text || '';
    let cleaned = typeof text === 'string' ? text : String(text);
    if (cleaned.startsWith('Element: ')) cleaned = cleaned.substring(9);
    else if (cleaned.startsWith('Element:')) cleaned = cleaned.substring(8);
    return cleaned.trim();
  }
  return String(item);
}

// Extract the AI justification from a requirement item
function extractAiAnswer(item: any): string {
  if (!item || typeof item !== 'object') return '';
  return item.ai_answer || '';
}

// Derive passing from score when not provided by backend
function isPassing(result: AnalysisResult): boolean {
  if (typeof result.passing === 'boolean') return result.passing;
  return (result.compliance_score || 0) >= 90;
}

const VideoResultMobile: React.FC = () => {
  const { analysis_id } = useParams<{ analysis_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showCompliant, setShowCompliant] = useState(true);
  const [showNonCompliant, setShowNonCompliant] = useState(true);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Campaign UUID — from URL ?campaign= param, or populated from backend result data
  const [campaignUuid, setCampaignUuid] = useState<string | null>(
    new URLSearchParams(location.search).get('campaign')
  );

  // Robust campaign UUID: prefer state (from URL or async fetch), fall back to loaded result data.
  // This avoids the timing gap where campaignUuid state is still null but result already loaded.
  const effectiveCampaignUuid = campaignUuid || result?.campaign_uuid || null;

  // Smart back navigation: determines the best place to go back to
  const goBack = () => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');

    // If brand user is logged in, go to campaign manager
    if (token && userType === 'brand') {
      navigate('/brand/campaign');
      return;
    }

    // If we know the campaign, go back to the upload page for that campaign
    if (effectiveCampaignUuid) {
      navigate(`/upload/${effectiveCampaignUuid}`);
      return;
    }

    // If there's meaningful browser history, use it
    if (window.history.length > 2) {
      navigate(-1);
      return;
    }

    // Ultimate fallback: go home
    navigate('/');
  };

  // Fetch result
  useEffect(() => {
    const fetchResult = async () => {
      if (!analysis_id) return;

      try {
        // Try campaign-analysis endpoint first (public, includes campaign info)
        const campaignAnalysisUrl = campaignUuid
          ? `${API_ROOT}/campaign-analysis/${analysis_id}?campaign_uuid=${campaignUuid}`
          : `${API_ROOT}/campaign-analysis/${analysis_id}`;
        
        // Helper to process and store result data
        const processResult = (data: any) => {
          console.log('[Mobile Result] Raw data received:', {
            keys: Object.keys(data),
            compliance_score: data.compliance_score,
            passing: data.passing,
            compliant_count: data.compliant?.length,
            non_compliant_count: data.non_compliant?.length,
            compliant_type: data.compliant?.[0] ? typeof data.compliant[0] : 'empty',
            non_compliant_type: data.non_compliant?.[0] ? typeof data.non_compliant[0] : 'empty',
            has_unique_id: !!data.unique_id,
            campaign_uuid: data.campaign_uuid,
            submission_status: data.submission_status,
          });
          // Ensure compliant/non_compliant are arrays
          if (!Array.isArray(data.compliant)) data.compliant = [];
          if (!Array.isArray(data.non_compliant)) data.non_compliant = [];
          // Derive passing if not present
          if (typeof data.passing !== 'boolean') {
            data.passing = (data.compliance_score || 0) >= 90;
          }
          setResult(data);
          if (data.campaign_uuid && !campaignUuid) {
            setCampaignUuid(data.campaign_uuid);
          }
          if (data.submission_status === 'submitted') {
            setSubmissionComplete(true);
          }
        };

        console.log('[Mobile Result] Fetching from:', campaignAnalysisUrl);
        let response = await fetch(campaignAnalysisUrl);
        
        if (response.ok) {
          const data = await response.json();
          processResult(data);
          return;
        }
        console.warn('[Mobile Result] campaign-analysis endpoint returned:', response.status);

        // Try authenticated endpoint (for brand/agency users viewing their campaign submissions)
        const token = localStorage.getItem('token');
        if (token) {
          console.log('[Mobile Result] Trying authenticated /analysis/ endpoint');
          response = await fetch(`${API_ROOT}/analysis/${analysis_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            processResult(data);
            return;
          }
          console.warn('[Mobile Result] /analysis/ endpoint returned:', response.status);
        }

        // All endpoints failed
        console.error('[Mobile Result] All endpoints failed for analysis_id:', analysis_id);
        setError('Result not found');
      } catch (err) {
        setError('Failed to load result');
      } finally {
        setIsLoading(false);
      }
    };

    fetchResult();
  }, [analysis_id, campaignUuid]);

  // Submit to campaign
  const handleSubmitToCampaign = async () => {
    if (!effectiveCampaignUuid || !analysis_id) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API_ROOT}/submit-campaign-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_id: analysis_id,
          campaign_uuid: effectiveCampaignUuid,
          creator_comments: [],
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Submission failed');
      }

      const responseData = await response.json();
      setResult(prev => prev ? {
        ...prev,
        submitted_at: responseData.submitted_at || new Date().toISOString(),
        submission_status: 'submitted',
      } : prev);
      setSubmissionComplete(true);
    } catch (err: any) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy share link
  const handleCopyLink = async () => {
    const link = `${window.location.origin}/campaign-result/${analysis_id}`;
    await navigator.clipboard.writeText(link);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Get score styling — matches desktop brand-campaign-manager.tsx
  const getScoreColor = (score: number) => {
    if (score >= 90) return { bg: PALETTE.inchworm, text: PALETTE.gunmetal };
    if (score >= 61) return { bg: '#fbbf24', text: PALETTE.gunmetal };
    return { bg: '#fca5a5', text: PALETTE.gunmetal };
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="mobile-app" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="mobile-spinner" />
        <p style={{ marginTop: 16, color: '#666' }}>Loading result...</p>
      </div>
    );
  }

  // Error state
  if (error || !result) {
    return (
      <div className="mobile-app" style={{ justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <AlertCircle size={48} color="#dc2626" />
        <h2 style={{ marginTop: 16, color: PALETTE.gunmetal }}>{error || 'Result not found'}</h2>
        <button
          className="mobile-btn mobile-btn-primary"
          onClick={goBack}
          style={{ marginTop: 24 }}
        >
          <ArrowLeft size={18} style={{ marginRight: 8 }} />
          Go Back
        </button>
      </div>
    );
  }

  const scoreColors = getScoreColor(result.compliance_score);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      overflow: 'hidden',
    }}>
      <SEO
        title="Video Analysis Results"
        description="View your BrandComply video compliance analysis results on mobile."
        path={`/result/${analysis_id}`}
      />
      {/* Header */}
      <div style={{ 
        background: PALETTE.gunmetal, 
        padding: '16px',
        paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <button
            onClick={goBack}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            <ArrowLeft size={22} color="#fff" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: '#fff', fontSize: 18, margin: 0 }}>
              {result.campaign_name || 'Video Result'}
            </h1>
            {result.creator_name && (
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, margin: '4px 0 0' }}>
                by {result.creator_name}
              </p>
            )}
          </div>
          <a
            href="https://brandcomply.ai"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 10,
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              flexShrink: 0,
              alignSelf: 'flex-end',
            }}
          >
            powered by <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>BrandComply</span>
          </a>
        </div>
      </div>

      {/* Content */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: 16,
        paddingBottom: (effectiveCampaignUuid && !submissionComplete)
          ? 'calc(80px + env(safe-area-inset-bottom, 0px))'
          : 'calc(32px + env(safe-area-inset-bottom, 0px))',
      }}>
        {/* Video Player */}
        {result.video_url && (
          <div style={{
            aspectRatio: '9/16',
            maxHeight: 280,
            background: '#000',
            borderRadius: 12,
            marginBottom: 20,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <video
              src={result.video_url}
              controls
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        )}

        {/* Score */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: scoreColors.bg,
            margin: '0 auto 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: PALETTE.gunmetal }}>
              {Math.round(result.compliance_score)}%
            </span>
          </div>

          <h2 style={{ 
            color: PALETTE.gunmetal, 
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
          }}>
            {isPassing(result) ? 'Compliant' : 'Needs Improvement'}
          </h2>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {result.approval_status && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 12px',
                borderRadius: 20,
                background: '#f0f0f0',
                color: PALETTE.gunmetal,
                fontSize: 13,
                fontWeight: 500,
              }}>
                {result.approval_status === 'approved' && (
                  <CheckCircle size={14} style={{ color: PALETTE.inchworm, flexShrink: 0 }} />
                )}
                {result.approval_status === 'revision_requested' && (
                  <RefreshCcw size={13} style={{ color: PALETTE.orange, flexShrink: 0 }} />
                )}
                {result.approval_status === 'approved' ? 'Approved' :
                 result.approval_status === 'revision_requested' ? 'Revision Requested' : 
                 'Pending Review'}
              </span>
            )}

            {submissionComplete && (
              <span style={{
                display: 'inline-block',
                padding: '5px 12px',
                borderRadius: 20,
                background: '#dcfce7',
                color: '#16a34a',
                fontSize: 13,
                fontWeight: 500,
              }}>
                ✓ Submitted
              </span>
            )}
          </div>
        </div>

        {/* Submission error */}
        {submitError && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 12,
            padding: 12,
            marginBottom: 16,
            color: '#dc2626',
            fontSize: 14,
            textAlign: 'center',
          }}>
            {submitError}
          </div>
        )}

        {/* Non-Compliant Items — shown first (most important for the user) */}
        {result.non_compliant && result.non_compliant.length > 0 && (
          <div className="mobile-card" style={{ marginBottom: 16, border: '1px solid rgba(220,38,38,0.15)' }}>
            <button
              onClick={() => setShowNonCompliant(!showNonCompliant)}
              style={{
                width: '100%',
                padding: 16,
                background: 'rgba(220,38,38,0.04)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderRadius: showNonCompliant ? '12px 12px 0 0' : 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <XCircle size={20} color="#dc2626" />
                <span style={{ fontWeight: 600, color: '#dc2626' }}>
                  Needs Work ({result.non_compliant.length})
                </span>
              </div>
              {showNonCompliant ? <ChevronUp size={20} color="#999" /> : <ChevronDown size={20} color="#999" />}
            </button>

            {showNonCompliant && (
              <div style={{ padding: '0 16px 16px' }}>
                {result.non_compliant.map((item, index) => {
                  const text = extractRequirementText(item);
                  const justification = extractAiAnswer(item);
                  if (!text) return null;
                  return (
                    <div
                      key={index}
                      style={{
                        padding: '12px 0',
                        borderBottom: index < result.non_compliant.length - 1 ? '1px solid #f0f0f0' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <XCircle size={16} color="#dc2626" style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, color: '#222', lineHeight: 1.5, fontWeight: 500 }}>{text}</span>
                      </div>
                      {justification && (
                        <div style={{
                          marginTop: 8,
                          marginLeft: 26,
                          padding: '8px 12px',
                          background: 'rgba(220,38,38,0.05)',
                          borderLeft: '3px solid #dc2626',
                          borderRadius: '0 8px 8px 0',
                          fontSize: 13,
                          color: '#555',
                          lineHeight: 1.5,
                        }}>
                          {justification}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Compliant Items */}
        {result.compliant && result.compliant.length > 0 && (
          <div className="mobile-card" style={{ marginBottom: 16, border: '1px solid rgba(22,163,106,0.15)' }}>
            <button
              onClick={() => setShowCompliant(!showCompliant)}
              style={{
                width: '100%',
                padding: 16,
                background: 'rgba(22,163,106,0.04)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderRadius: showCompliant ? '12px 12px 0 0' : 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle size={20} color="#16a34a" />
                <span style={{ fontWeight: 600, color: '#16a34a' }}>
                  Compliant ({result.compliant.length})
                </span>
              </div>
              {showCompliant ? <ChevronUp size={20} color="#999" /> : <ChevronDown size={20} color="#999" />}
            </button>

            {showCompliant && (
              <div style={{ padding: '0 16px 16px' }}>
                {result.compliant.map((item, index) => {
                  const text = extractRequirementText(item);
                  const justification = extractAiAnswer(item);
                  if (!text) return null;
                  return (
                    <div
                      key={index}
                      style={{
                        padding: '12px 0',
                        borderBottom: index < result.compliant.length - 1 ? '1px solid #f0f0f0' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <Check size={16} color="#16a34a" style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, color: '#444', lineHeight: 1.5 }}>{text}</span>
                      </div>
                      {justification && (
                        <div style={{
                          marginTop: 8,
                          marginLeft: 26,
                          padding: '8px 12px',
                          background: 'rgba(22,163,106,0.05)',
                          borderLeft: '3px solid #16a34a',
                          borderRadius: '0 8px 8px 0',
                          fontSize: 13,
                          color: '#555',
                          lineHeight: 1.5,
                        }}>
                          {justification}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Meta Info */}
        {(result.creator_email || result.submitted_at) && (
          <div className="mobile-card">
            <div className="mobile-card-content">
              <h4 style={{ fontSize: 14, color: '#666', margin: '0 0 12px' }}>Details</h4>
              
              {result.creator_email && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#999' }}>Creator Email</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: PALETTE.gunmetal }}>
                    {result.creator_email}
                  </p>
                </div>
              )}

              {result.submitted_at && (
                <div>
                  <span style={{ fontSize: 13, color: '#999' }}>Submitted</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: PALETTE.gunmetal }}>
                    {new Date(result.submitted_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Action Bar — visible for campaign results before submission */}
      {effectiveCampaignUuid && !submissionComplete && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid #e5e5e5',
          padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          gap: 10,
          flexShrink: 0,
        }}>
          <button
            onClick={handleSubmitToCampaign}
            disabled={isSubmitting}
            style={{
              flex: 2,
              background: PALETTE.inchworm,
              color: PALETTE.gunmetal,
              border: 'none',
              borderRadius: 12,
              padding: '14px 0',
              fontSize: 15,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="mobile-spinner" style={{ width: 18, height: 18 }} />
                Submitting...
              </>
            ) : (
              <>
                <Send size={18} />
                Submit Video
              </>
            )}
          </button>
          <button
            onClick={() => navigate(`/upload/${effectiveCampaignUuid}`)}
            style={{
              flex: 1,
              background: 'transparent',
              border: `2px solid ${PALETTE.gunmetal}`,
              color: PALETTE.gunmetal,
              borderRadius: 12,
              padding: '14px 0',
              fontSize: 15,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: 'pointer',
            }}
          >
            <Upload size={18} />
            Re-upload
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoResultMobile;
